import { useEffect, useLayoutEffect, useRef, useState, useTransition } from "react";
import { Routes, useLocation, useNavigationType, UNSAFE_LocationContext as LocationContext } from "react-router-dom";
import { pageHidesSidebar } from "../utils/sidebarPages";

// Pages that hide the sidebar nav (the admin inner pages and the /app
// secondary pages) behave like full-screen modals, so they open and close
// with the same animations Ionic's modal uses (what the Tutorials modal
// does): on md the page rises 40px while fading in over 280ms and fades
// while dropping 40px over 200ms on the way out; on ios it slides up from
// and back down off the bottom over 500ms. Leaving one holds the route swap
// until the dismiss ends, and the outgoing page keeps seeing its own
// location meanwhile, so the back arrow, browser back and any other
// navigation away all close the same way.

const ionicMode = () =>
  (typeof document !== "undefined" && document.documentElement.getAttribute("mode")) || "md";

// Ionic's ion-modal leave animation (createLeaveAnimation / mdLeaveAnimation
// and the ios variant), replayed on a page element. Resolves with the
// Animation so the caller can cancel its forward fill once the page is gone.
export function playDismiss(el) {
  return new Promise((resolve) => {
    if (!el || typeof el.animate !== "function") { resolve(null); return; }
    const ios = ionicMode() === "ios";
    let anim = null;
    let done = false;
    const finish = () => { if (!done) { done = true; resolve(anim); } };
    try {
      anim = el.animate(
        ios
          ? [{ transform: "translateY(0vh)" }, { transform: "translateY(100vh)" }]
          : [{ opacity: 0.99, transform: "translateY(0px)" }, { opacity: 0, transform: "translateY(40px)" }],
        {
          duration: ios ? 500 : 200,
          easing: ios ? "cubic-bezier(0.32,0.72,0,1)" : "cubic-bezier(0.47,0,0.745,0.715)",
          fill: "forwards",
        },
      );
      anim.onfinish = finish;
      anim.oncancel = finish;
    } catch { finish(); }
    setTimeout(finish, ios ? 650 : 350); // never hold navigation hostage
  });
}

// Ionic's ion-modal enter animation (createEnterAnimation / mdEnterAnimation
// and the ios variant), replayed on the page that has just opened.
export function playPresent(el) {
  if (!el || typeof el.animate !== "function") return null;
  const ios = ionicMode() === "ios";
  try {
    return el.animate(
      ios
        ? [{ transform: "translateY(100vh)" }, { transform: "translateY(0vh)" }]
        : [{ opacity: 0.01, transform: "translateY(40px)" }, { opacity: 1, transform: "translateY(0px)" }],
      {
        duration: ios ? 500 : 280,
        easing: ios ? "cubic-bezier(0.32,0.72,0,1)" : "cubic-bezier(0.36,0.66,0.04,1)",
      },
    );
  } catch { return null; }
}

const pageEl = () => document.getElementById("admin-main") || document.getElementById("app-main");

export default function DismissRoutes({ children }) {
  const location = useLocation();
  const navigationType = useNavigationType();
  const [shown, setShown] = useState(() => ({ location, navigationType }));
  // isPending: a lazy page's chunk is still downloading while the old page
  // holds - the thin bar below says "working on it" so the click never
  // feels ignored.
  const [isPending, startTransition] = useTransition();
  const latest = useRef({ location, navigationType });
  const pending = useRef(false);
  const anim = useRef(null);
  latest.current = { location, navigationType };

  useEffect(() => {
    if (location === shown.location) return;
    if (pending.current) return; // the running dismiss applies the latest location when it ends
    const from = shown.location.pathname;
    if (!pageHidesSidebar(from) || from === location.pathname) {
      // A transition keeps the CURRENT page on screen while a lazy route's
      // chunk downloads, instead of dropping to the Suspense fallback (the
      // white flash between admin pages).
      startTransition(() => setShown({ location, navigationType }));
      return;
    }
    pending.current = true;
    playDismiss(pageEl()).then((a) => {
      anim.current = a;
      pending.current = false;
      startTransition(() => setShown(latest.current));
    });
  }, [location]); // eslint-disable-line

  // Once the next page has committed (before it paints): drop the dismiss
  // animation's forward fill — the outgoing element is normally unmounted by
  // then, but a route that reuses the same component (e.g. one editor id to
  // another) keeps its element and must become visible again — and, when the
  // page that just opened is one that hides the sidebar, play the modal
  // enter animation on it. A fresh page load does not animate in, only an
  // in-app navigation does, the way the Tutorials modal only opens on a tap.
  const prevShown = useRef(null);
  useLayoutEffect(() => {
    if (anim.current) { try { anim.current.cancel(); } catch { /* already gone */ } anim.current = null; }
    const prev = prevShown.current;
    prevShown.current = shown;
    if (prev && prev.location.pathname !== shown.location.pathname && pageHidesSidebar(shown.location.pathname)) {
      playPresent(pageEl());
    }
  }, [shown]);

  return (
    <LocationContext.Provider value={shown}>
      {isPending && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, height: 3, zIndex: 99999, background: "rgba(52,211,153,0.15)", overflow: "hidden", pointerEvents: "none" }}>
          <div style={{ position: "absolute", top: 0, bottom: 0, width: "40%", background: "var(--ion-color-primary, #34d399)", borderRadius: 3, animation: "route-pending 0.9s ease-in-out infinite" }} />
          <style>{"@keyframes route-pending { 0% { left: -40%; } 100% { left: 100%; } }"}</style>
        </div>
      )}
      <Routes location={shown.location}>{children}</Routes>
    </LocationContext.Provider>
  );
}
